import React from "react";
import "../../nav/Navigation.css";
import {Link} from "react-router-dom";
import {BadgeStatus, BadgeType, Session, User, UserBadge, Visit} from "../../types/Types";
import VerificationTag from "../../tag/VerificationTag";
import {getDateFromTimeStamp, truncateString} from "../../util/Util";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faStar} from "@fortawesome/free-solid-svg-icons";
import {getBadgeIcon} from "../../util/BadgeUtil";
import V2PhotoOverlayChip from "../../shared/v2/V2PhotoOverlayChip";

interface Props {
    user: User | undefined
    visit: Visit | undefined
    session: Session | undefined
    padRight?: Boolean
    hideLocationDot: Boolean
    linksToTab: String
    displayBonus?: Boolean
    chipClassName?: string
    photoOverlay?: boolean
}


function UserTag(props: Props) {
    const displayBonus = props.displayBonus ?? false;
    const photoOverlayClassName = props.photoOverlay ? "v2-photo-overlay-chip" : "";
    const chipClassName = [props.chipClassName, photoOverlayClassName].filter(Boolean).join(" ");

    const isCheckedIn = (props.session && getDateFromTimeStamp(props.session.checkOutTime) > new Date()) || false;
    const shouldDisplayLocationDot = (((props.visit && props.visit.verified) || isCheckedIn) && !props.hideLocationDot);

    const activeBadges: UserBadge[] = props.user?.badgeDisabled
        ? []
        : (props.user?.badges || [])
            .filter(b => b.status === BadgeStatus.ACTIVE)
            .sort((a, b) => a.order - b.order);

    const padRight = activeBadges.length > 0 && !!props.padRight;
    const userTagWrapperClassName = [padRight ? "mr-1" : "", props.photoOverlay ? "v2-photo-user-tag" : ""].filter(Boolean).join(" ");
    const userLinkClassName = [
        props.photoOverlay ? "p-0" : "p-0 mr-1",
        (props.photoOverlay || props.chipClassName) ? "v2-user-tag-link" : ""
    ].filter(Boolean).join(" ");
    const badgeWrapperClassName = props.photoOverlay ? "d-inline-block" : "d-inline-block mr-1";
    const verificationWrapperClassName = props.photoOverlay ? "d-inline-block" : "d-inline-block mr-1";

    const renderChip = (content: React.ReactNode, badge = false) => {
        const classes = `${props.photoOverlay ? "" : "user-tag"} ${isCheckedIn ? "active-session" : ""} ${chipClassName} ${badge && props.chipClassName ? "v2-glass-badge-chip" : ""}`;

        if (props.photoOverlay) {
            return <V2PhotoOverlayChip className={classes}>{content}</V2PhotoOverlayChip>;
        }

        return <div className={classes}>{content}</div>;
    };

    return (
        <div className={userTagWrapperClassName}>
            {props.user && <Link to={props.linksToTab ? `/profile/${props.user.id}/${props.linksToTab}` : `/profile/${props.user.id}`} className={userLinkClassName}>
                    {renderChip(<div className={"d-inline-block"}>{truncateString(props.user.name)}</div>)}
            </Link>}
            {activeBadges.map(badge => {
                const badgeContent = badge.badgeType === BadgeType.CHAMPION ? (() => {
                    const year = badge.description.match(/\d{4}/)?.[0];
                    return <small><FontAwesomeIcon icon={getBadgeIcon(badge.badgeType)} />{year ? <span className="ml-0"> '{year.slice(-2)}</span> : null}</small>;
                })() : <FontAwesomeIcon icon={getBadgeIcon(badge.badgeType)} />;
                const content = props.photoOverlay
                    ? <span className="v2-photo-overlay-badge-content">{badgeContent}</span>
                    : badgeContent;

                return (
                    <div key={badge.id ?? badge.badgeType} className={badgeWrapperClassName}>
                        {renderChip(content, true)}
                    </div>
                );
            })}
            {shouldDisplayLocationDot && <div className={verificationWrapperClassName}>
                <VerificationTag active={isCheckedIn} chipClassName={chipClassName}/>
            </div>}
            {displayBonus && <small className="text-truncate">
                <FontAwesomeIcon className="ml-1 animated zoomIn icon-bonus" icon={faStar} />
            </small>}
        </div>
    );
}

export default UserTag;
